import {firestore, messaging} from "@/firebase/index.js"
import {getToken, isSupported, onMessage} from "firebase/messaging"
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore"
import {useNotificationsStore} from "../stores/notifications"
import {storeToRefs} from "pinia"

export function useFirebaseMessaging(args) {
  async function initMessaging() {
    const notificationsStore = useNotificationsStore()
    const {notifications} = storeToRefs(notificationsStore)

    const supported = await isSupported()
    if (!supported) {
      notifications.value.push({
        debug: "messaging not supported",
      })
      console.warn("firebase messaging is not supported by this browser")
      return
    }

    notifications.value.push({
      debug: "messaging supported",
    })

    if (Notification.permission == "granted") {
      // console.log("notification permission was granted")
      notifications.value.push({
        debug: "notification permission was granted",
      })
      const msg = await messaging

      notifications.value.push({
        debug: "registering messaging",
      })

      await registerMessaging()

      console.log("listening to messages [frontend]")
      notifications.value.push({
        debug: "listening to messages [frontend]",
      })
      onMessage(msg, (payload) => {
        console.log("message received [frontend]:", payload)
        notifications.value.push({
          payload,
        })
      })
    } else {
      // console.log("notification permission was not granted (yet)")
    }
  }

  async function registerMessaging() {
    const notificationsStore = useNotificationsStore()
    const {notifications} = storeToRefs(notificationsStore)
    // console.log("registerMessaging()")
    notifications.value.push({
      debug: "registerMessaging()",
    })

    // register custom service worker

    const serviceWorkerDir = import.meta.env.VITE_ENVIRONMENT == "local_dev" ? "/src" : "/js"
    const serviceWorkerModule = "firebaseMessagingSW.js"
    const serviceWorkerPath = `${serviceWorkerDir}/${serviceWorkerModule}`

    notifications.value.push({
      debug: "service worker path",
      detail: serviceWorkerPath,
    })

    const registeredServiceWorkers = await navigator.serviceWorker.getRegistrations()
    let serviceWorkerRegistration = registeredServiceWorkers.find((sw) =>
      sw.active.scriptURL.endsWith(serviceWorkerPath)
    )

    if (!serviceWorkerRegistration) {
      serviceWorkerRegistration = await navigator.serviceWorker.register(serviceWorkerPath, {
        type: "module",
      })
      notifications.value.push({
        debug: "registered new service worker",
      })
      // console.log("registered new service worker:", serviceWorkerRegistration)
    } else {
      // console.log("already have service worker running")
      notifications.value.push({
        debug: "already have service worker running",
        detail: serviceWorkerRegistration,
      })
    }

    // console.log("auth:", auth.currentUser)

    const vapidKey = import.meta.env.VITE_FIREBASE_MESSAGING_VAPID_KEY
    const msg = await messaging

    let token = await getToken(msg, {vapidKey, serviceWorkerRegistration})

    // let token
    if (token) {
      // console.log("client token:")
      // console.log(token)

      await saveUserToken(token)
    } else {
      console.warn("no token available, request permission to generate")
    }

    return
  }

  async function saveUserToken(token) {
    let tokenAlreadySaved = await isTokenAlreadySaved(token)

    if (tokenAlreadySaved) {
      await updateUserToken(tokenAlreadySaved)
    } else {
      const fbMessagingTokensRef = collection(firestore, `users/${args.userId}/fbMessagingTokens`)
      const data = {
        token,
        meta: {
          created: serverTimestamp(),
          updated: false,
          deleted: false,
        },
      }
      await addDoc(fbMessagingTokensRef, data).catch((e) =>
        console.error("error saving user token:", e)
      )
    }

    return
  }

  async function updateUserToken(tokenAlreadySaved) {
    const tokenRef = doc(firestore, `users/${args.userId}/fbMessagingTokens/${tokenAlreadySaved}`)
    const tokenData = {
      "meta.updated": serverTimestamp(),
    }
    await updateDoc(tokenRef, tokenData)
  }

  async function isTokenAlreadySaved(token) {
    const collectionRef = collection(firestore, `users/${args.userId}/fbMessagingTokens`)
    const q = query(collectionRef, where("token", "==", token))
    let foundToken = await getDocs(q)
    if (foundToken.docs.length) {
      return foundToken.docs[0].id
    } else {
      return false
    }
  }

  return {initMessaging}
}
